import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

class TokenManager {
  constructor(msalInstance) {
    this.msalInstance = msalInstance;
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.listeners = new Set();

    // Start monitoring when initialized
    this.startTokenMonitoring();
  }

  // Subscribe to token events
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notify all listeners of token changes
  notifyListeners(token, error = null) {
    this.listeners.forEach(callback => callback(token, error));
  }

  // Get current token from storage
  getCurrentToken() {
    return localStorage.getItem('msal.token');
  }

  // Store token and notify listeners
  setToken(token) {
    if (token) {
      localStorage.setItem('msal.token', token);
      this.notifyListeners(token);
    } else {
      localStorage.removeItem('msal.token');
      this.notifyListeners(null);
    }
  }

  // Check if token is expired or about to expire
  isTokenExpiringSoon(token, bufferMinutes = 5) {
    if (!token) return true;

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds

      return (expirationTime - currentTime) < bufferTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }

  // Refresh token silently
  async refreshToken() {
    if (this.isRefreshing) {
      // If already refreshing, return the existing promise
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          throw new Error('No accounts available');
        }

        const account = this.msalInstance.getActiveAccount() || accounts[0];


        const tokenResponse = await this.msalInstance.acquireTokenSilent({
          ...apiRequest,
          account,
          forceRefresh: true // Force refresh to get a new token
        });

        const newToken = tokenResponse.accessToken;
        this.setToken(newToken);


        // Schedule next refresh
        this.scheduleNextRefresh(newToken);

        return newToken;
      } catch (error) {
        console.error('❌ Token refresh failed:', error);

        // If silent refresh fails, try interactive refresh
        if (error instanceof InteractionRequiredAuthError) {
          console.log('🔄 Interactive token refresh required');
          try {
            await this.msalInstance.acquireTokenRedirect({
              ...apiRequest,
              account: this.msalInstance.getActiveAccount()
            });
            // Note: This will cause a redirect, so execution stops here
          } catch (interactiveError) {
            console.error('❌ Interactive token refresh failed:', interactiveError);
            this.setToken(null);
            throw interactiveError;
          }
        } else {
          this.setToken(null);
          throw error;
        }
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Schedule next token refresh
  scheduleNextRefresh(token) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!token) return;

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();

        // Calculate time until refresh (refresh 10 minutes before expiration)
        const refreshTime = Math.max(0, expirationTime - currentTime - (10 * 60 * 1000));

        console.log(`⏰ Next token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);

        this.refreshTimer = setTimeout(async () => {
          try {
            await this.refreshToken();
          } catch (error) {
            console.error('Scheduled token refresh failed:', error);
          }
        }, refreshTime);
      }
    } catch (error) {
      console.error('Error scheduling token refresh:', error);
      // Fallback: refresh every 30 minutes
      this.refreshTimer = setTimeout(() => this.refreshToken(), 30 * 60 * 1000);
    }
  }

  // Start monitoring token expiration
  startTokenMonitoring() {
    const token = this.getCurrentToken();

    if (token && !this.isTokenExpiringSoon(token, 10)) {
      // Token is still valid, schedule refresh
      this.scheduleNextRefresh(token);
    } else if (token) {
      // Token is expiring soon, refresh immediately
      this.refreshToken();
    }

    // Add visibility change listener to refresh tokens when user returns to tab
    this.setupVisibilityListener();

    // Add periodic health check (every 5 minutes)
    this.setupHealthCheck();
  }

  // Setup visibility change listener
  setupVisibilityListener() {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 User returned to tab, checking token health');
        const token = this.getCurrentToken();
        if (token && this.isTokenExpiringSoon(token, 5)) {
          console.log('🔄 Token expiring soon after visibility change, refreshing');
          this.refreshToken();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Store reference for cleanup
    this.visibilityListener = handleVisibilityChange;
  }

  // Setup periodic health check
  setupHealthCheck() {
    this.healthCheckInterval = setInterval(() => {
      const token = this.getCurrentToken();
      if (token) {
        const isExpiring = this.isTokenExpiringSoon(token, 15);
        if (isExpiring) {
          console.log('🏥 Health check: Token expiring soon, refreshing proactively');
          this.refreshToken();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  // Ensure we have a valid token
  async ensureValidToken() {
    const currentToken = this.getCurrentToken();

    if (!currentToken || this.isTokenExpiringSoon(currentToken, 2)) {
      console.log('🔄 Token invalid or expiring soon, refreshing...');
      return await this.refreshToken();
    }

    return currentToken;
  }

  // Stop monitoring
  stopTokenMonitoring() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }

    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }

  }

  // Clean up resources
  destroy() {
    this.stopTokenMonitoring();
    this.listeners.clear();
  }
}

export default TokenManager;
